/**
 * Stuck-payments badge — rendered in the Dashboard and Settlements page
 * headers. Polls ``GET /api/settings/sumup/transactions/stuck-count``
 * every 30 seconds while the tab is foregrounded and links to the SumUp
 * transactions view pre-filtered to the syncing window.
 *
 * "Stuck" = paid but not yet settled (the recoverable window the poller
 * retries). A non-zero count means a card was charged and is being
 * finalized; it is reassuring (money is safe), not alarming, so the copy
 * stays calm. Renders NOTHING when the count is 0.
 *
 * The stuck-count endpoint is Owner-only; for non-owners the fetch 403s
 * and we simply render nothing (no toast, no badge) — same quiet-failure
 * posture as the catalog drift badge.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Tooltip } from "antd";
import { SyncOutlined } from "@ant-design/icons";

import { api } from "../api/client";

const POLL_MS = 30_000;

/** Deep link into the transactions view filtered to paid-but-unsettled. */
const STUCK_LINK = "/settings/sumup/transactions?status=stuck";

export const SumupStuckBadge = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await api.sumupStuckCount();
        if (!cancelled) setCount(r.count);
      } catch {
        // Quiet: Owner-only endpoint (403 for non-owners) + best-effort
        // cosmetic. A failure should never pop a global toast or badge.
        if (!cancelled) setCount(0);
      }
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  if (count === 0) return null;
  return (
    <Tooltip
      title={`${count} card payment${
        count === 1 ? "" : "s"
      } secured and syncing — click to review`}
    >
      <span
        style={{
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
        onClick={() => navigate(STUCK_LINK)}
      >
        <SyncOutlined spin style={{ color: "var(--ant-color-warning)" }} />
        <Badge
          count={count}
          overflowCount={99}
          style={{ backgroundColor: "var(--ant-color-warning)" }}
        />
      </span>
    </Tooltip>
  );
};

export default SumupStuckBadge;
